'use client';

import {
  Box,
  Button,
  Center,
  Drawer,
  Flex,
  HStack,
  IconButton,
  Portal,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { Monitor, Play } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';
import { SidePanelToggleButton } from '@/components/core/app-layout/SidePanelToggleButton';
import {
  SidePanelResizeHandle,
  useSidePanelSize,
} from '@/components/core/app-layout/SidePanelSize';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import {
  useLivingDocsPanelActions,
  useLivingDocsPanelClose,
} from '@/contexts/LivingDocsPanelContext';
import { GitHubLivePreview, type WebsitePreviewMetadata } from './GitHubLivePreview';

const PREVIEW_LAUNCH_TIMEOUT_MS = 30_000;

export const WorkspaceWebsitePreviewPanel = memo(function WorkspaceWebsitePreviewPanel({
  preview,
  open,
  onOpenChange,
  onLaunchPreview,
}: {
  preview: WebsitePreviewMetadata | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunchPreview: () => Promise<boolean>;
}) {
  const {
    setWebsitePreviewBrowserContext,
    registerWebsitePreviewSelectionClear,
    updateWebsitePreview,
  } = useLivingDocsPanelActions();
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const { panelWidthValue: sharedPanelWidth } = useSidePanelSize();
  const [launchPending, setLaunchPending] = useState(false);
  const [launchTimedOut, setLaunchTimedOut] = useState(false);
  const previewId = preview?.id || null;
  const handleBrowserContextChange = useCallback(
    (context: Parameters<typeof setWebsitePreviewBrowserContext>[1]) => {
      if (previewId) setWebsitePreviewBrowserContext(previewId, context);
    },
    [previewId, setWebsitePreviewBrowserContext]
  );

  useEffect(() => {
    if (!open) {
      setLaunchPending(false);
      setLaunchTimedOut(false);
    } else if (preview) {
      setLaunchPending(false);
      setLaunchTimedOut(false);
    }
  }, [open, preview]);

  useEffect(() => {
    if (!open || preview || !launchPending) return;
    const timeout = window.setTimeout(() => {
      setLaunchTimedOut(true);
    }, PREVIEW_LAUNCH_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [launchPending, open, preview]);

  const handleLaunchPreview = useCallback(async () => {
    if (launchPending) return;
    setLaunchTimedOut(false);
    setLaunchPending(true);
    try {
      if (!(await onLaunchPreview())) setLaunchPending(false);
    } catch {
      setLaunchPending(false);
    }
  }, [launchPending, onLaunchPreview]);

  useLivingDocsPanelClose(() => onOpenChange(false), open);

  if (!open) return null;

  const panelContent = preview ? (
    <GitHubLivePreview
      preview={preview}
      variant="rail"
      onClose={() => onOpenChange(false)}
      onPreviewChange={updateWebsitePreview}
      onBrowserContextChange={handleBrowserContextChange}
      onSelectionClearHandlerChange={registerWebsitePreviewSelectionClear}
    />
  ) : (
    <Flex direction="column" h="full" minH={0}>
      <HStack
        minH="54px"
        px="3"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        bg="ui.surface.rail"
        justify="space-between"
        flexShrink={0}
      >
        <HStack gap="2">
          <Monitor size={17} aria-hidden="true" />
          <Text fontSize="13px" fontWeight="550">
            Website preview
          </Text>
        </HStack>
        <SidePanelToggleButton
          open
          label="Close website preview"
          onClick={() => onOpenChange(false)}
        />
      </HStack>
      <Center flex="1" minH={0} p={{ base: 6, md: 10 }} bg="ui.surface.rail">
        <VStack gap="4" maxW="360px" textAlign="center">
          <Center boxSize="52px" layerStyle="iconTile">
            {launchPending ? (
              <Spinner size="md" color="accent.fg" />
            ) : (
              <Monitor size={23} aria-hidden="true" />
            )}
          </Center>
          <Box>
            <Text fontSize="md" fontWeight="750">
              {launchPending
                ? 'Starting site editor'
                : launchTimedOut
                  ? 'Preview did not open'
                  : 'Launch your Astro site'}
            </Text>
            <Text mt="1.5" fontSize="sm" color="ui.text.secondary" lineHeight="1.5">
              {launchPending
                ? launchTimedOut
                  ? 'This is taking longer than usual. Attune is still preparing the private workspace; the preview will open when the request finishes.'
                  : 'Attune is preparing the private site workspace. The preview will appear here as soon as it is ready.'
                : launchTimedOut
                  ? 'The preview request failed. You can retry the launch.'
                  : 'Attune will start a private preview of the connected GitHub repository and open it here when it is ready.'}
            </Text>
          </Box>
          {!launchPending ? (
            <Button size="sm" onClick={handleLaunchPreview}>
              <Play size={15} aria-hidden="true" />
              {launchTimedOut ? 'Retry preview' : 'Launch preview'}
            </Button>
          ) : null}
        </VStack>
      </Center>
    </Flex>
  );

  if (!isDesktop) {
    return (
      <Drawer.Root open={open} onOpenChange={event => onOpenChange(event.open)} placement="end">
        <Portal>
          <Drawer.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Drawer.Positioner>
            <Drawer.Content
              data-section="workspaceWebsitePreviewPanel"
              w="full"
              maxW="full"
              h="100dvh"
              maxH="100dvh"
              overflow="hidden"
              bg="ui.surface.rail"
            >
              <Drawer.Title srOnly>Website preview</Drawer.Title>
              <Drawer.Description srOnly>
                Review and inspect the private website preview.
              </Drawer.Description>
              <Drawer.Body p="0" minH={0} overflow="hidden">
                {panelContent}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    );
  }

  return (
    <Flex
      as="aside"
      aria-label="Website preview"
      data-section="workspaceWebsitePreviewPanel"
      position="relative"
      zIndex="1"
      direction="column"
      w={sharedPanelWidth}
      minW={sharedPanelWidth}
      flexShrink={0}
      h="full"
      minH={0}
      borderLeftWidth="1px"
      borderColor="ui.border.subtle"
      bg="ui.surface.rail"
      boxShadow="var(--attune-shadow-elevated, none)"
      overscrollBehavior="contain"
    >
      <SidePanelResizeHandle label="Resize website preview" />
      {panelContent}
    </Flex>
  );
});
